import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, App, Select, Space, InputNumber, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { 
  getPreparationById, 
  createPreparation, 
  updatePreparation, 
  checkPreparationTitleExists 
} from '../../services/preparations';
import { listCatalog } from '../../services/catalogs';
import { listExercisesVariants } from '../../services/exercises';

const { Text, Title } = Typography;

const PreparationUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  // Catálogos
  const [preparationTypes, setPreparationTypes] = useState([]);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [exercisesList, setExercisesList] = useState([]);

  // Lista de ejercicios agregados
  const [exercises, setExercises] = useState([]);

  // Formulario para nuevo ejercicio
  const [newExercise, setNewExercise] = useState({
    exerciseKey: undefined,
    exerciseId: undefined,
    equipmentId: undefined,
    measurementTypeId: undefined,
    value: undefined,
  });

  // Carga de catálogos
  useEffect(() => {
    const loadCatalogsAndExercises = async () => {
      try {
        const [prepTypes, measTypes, exercisesData] = await Promise.all([
          listCatalog('preparation_types'),
          listCatalog('measurement_types'),
          listExercisesVariants(),
        ]);

        setPreparationTypes(prepTypes || []);
        setMeasurementTypes(measTypes || []);
        setExercisesList(exercisesData || []);
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
        message.error('Error al cargar los catálogos de apoyo');
      }
    };

    loadCatalogsAndExercises();
  }, [message]);

  // Carga de datos para edición
  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);

    getPreparationById(id, true)
      .then((data) => {
        form.setFieldsValue({
          title: data.title,
          preparation_type_id: data.preparation_type_id || data.type?.id,
        });

        if (data.exercises && data.exercises.length > 0) {
          setExercises(
            data.exercises.map((item) => {
              // Buscar la combinación exacta ejercicio + equipo
              const catalogExercise = exercisesList.find(
                (ex) =>
                  ex.exercise_id === item.exercise_id &&
                  (ex.equipment_id ?? null) === (item.equipment_id ?? null)
              );

              return {
                id: item.id,
                exercise_id: item.exercise_id,
                equipment_id: item.equipment_id ?? null,
                exercise_title:
                  catalogExercise?.displayLabel ||
                  item.exercise?.title ||
                  item.title ||
                  'Ejercicio',
                measurement_type_id:
                  item.measurement_type_id || item.measurement_type?.id,
                value: item.value,
              };
            })
          );
        }
      })
      .catch(() => {
        message.error('No se pudo cargar la información de la mini-rutina');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, isEdit, form, message, exercisesList]);

  const validateTitle = useCallback(async (_, value) => {
    if (!value || !value.trim()) return Promise.resolve();

    const alphanumericRegex = /^[A-Za-z0-9ÁÉÍÓÚÜÑáéíóúüñ\s]+$/;
    if (!alphanumericRegex.test(value.trim())) {
      return Promise.reject(new Error('El nombre solo puede contener letras, números y espacios'));
    }

    return Promise.resolve();
  }, []);

  // Agregar nuevo ejercicio a la lista
  const handleAddExercise = () => {
    if (!newExercise.exerciseId) {
      message.warning('Por favor selecciona un ejercicio');
      return;
    }

    if (!newExercise.measurementTypeId) {
      message.warning('Por favor selecciona un tipo de medición');
      return;
    }

    if ( !newExercise.value || newExercise.value <= 0 || !Number.isInteger(newExercise.value) ) {
      message.warning('Ingresa un entero positivo mayor a cero');
      return;
    }

    // Validar combinación ejercicio + equipo
    if (
      exercises.some(
        (item) =>
          item.exercise_id === newExercise.exerciseId &&
          (item.equipment_id ?? null) === (newExercise.equipmentId ?? null)
      )
    ) {
      message.warning('Este ejercicio con este equipo ya fue agregado a la lista');
      return;
    }

    const selectedExercise = exercisesList.find(
      (ex) =>
        ex.exercise_id === newExercise.exerciseId &&
        (ex.equipment_id ?? null) === (newExercise.equipmentId ?? null)
    );

    setExercises([
      ...exercises,
      {
        id: `temp-${Date.now()}`,
        exercise_id: newExercise.exerciseId,
        equipment_id: newExercise.equipmentId ?? null,
        exercise_title: selectedExercise?.displayLabel || 'Ejercicio',
        measurement_type_id: newExercise.measurementTypeId,
        value: newExercise.value,
      },
    ]);

    setNewExercise({
      exerciseKey: undefined,
      exerciseId: undefined,
      equipmentId: undefined,
      measurementTypeId: undefined,
      value: undefined,
    });
  };

  // Edición directa de tipo de medición en la lista
  const handleUpdateExerciseMeasurement = (index, measurementTypeId) => {
    const updated = [...exercises];
    updated[index].measurement_type_id = measurementTypeId;
    setExercises(updated);
  };

  // Edición directa de valor en la lista (validando entero > 0)
  const handleUpdateExerciseValue = (index, value) => {
    const updated = [...exercises];
    updated[index].value = value;
    setExercises(updated);
  };

  const handleRemoveExercise = (index) => {
    setExercises(exercises.filter((_, i) => i !== index));
  };

  const onSubmit = async (values) => {
    // Validar requerimiento de ejercicios
    if (exercises.length === 0) {
      message.error('Debes agregar al menos un ejercicio a la mini-rutina');
      return;
    }

    // Validar valores editados en la lista
    const hasInvalidValue = exercises.some(
      (item) => !item.value || item.value <= 0 || !Number.isInteger(item.value)
    );

    if (hasInvalidValue) {
      message.error('Todos los ejercicios deben tener un valor entero positivo mayor a 0');
      return;
    }

    try {
      setLoading(true);

      const exists = await checkPreparationTitleExists(values.title.trim(), isEdit ? Number(id) : null);
      if (exists) {
        message.error('Este nombre ya existe. Por favor, elige otro.');
        setLoading(false);
        return;
      }

      const exercisesPayload = exercises.map((item, index) => {
        const payloadItem = {
          exercise_id: item.exercise_id,
          equipment_id: item.equipment_id ?? null,
          measurement_type_id: item.measurement_type_id,
          value: item.value,
          display_order: index + 1,
        };

        // Mantener el ID de la tabla pivot si no es un registro temporal
        if (item.id && !item.id.toString().startsWith('temp-')) {
          payloadItem.id = Number(item.id);
        }

        return payloadItem;
      });

      const payload = {
        title: values.title.trim(),
        preparation_type_id: values.preparation_type_id,
        exercises: exercisesPayload,
      };

      if (isEdit && id) {
        await updatePreparation(id, payload);
        message.success('Actualizado correctamente');
      } else {
        await createPreparation(payload);
        message.success('Creado correctamente');
      }

      navigate('/preparations/all');
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Error al guardar';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '16px 32px' }}>
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 24 }}
      >
        <Col>
          <Space align="center">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            />

            <Text type="secondary">
              Calentamiento/Estiramiento
            </Text>
          </Space>

          <Title
            level={3}
            style={{ margin: 0, marginTop: 4 }}
          >
            {isEdit
              ? 'Editar Calentamiento'
              : 'Nuevo Calentamiento'}
          </Title>
        </Col>

        <Col>
          <Space>
            <Button
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancelar
            </Button>

            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={loading}
            >
              Guardar
            </Button>
          </Space>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        autoComplete="off"
      >
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 12,
          }}
        >
          <Title
            level={5}
            style={{
              marginTop: 0,
              marginBottom: 16,
            }}
          >
            Datos de la mini-rutina
          </Title>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nombre"
                name="title"
                rules={[
                  {
                    required: true,
                    message: 'El nombre es requerido',
                  },
                  {
                    validator: validateTitle,
                  },
                ]}
              >
                <Input
                  placeholder="Ej. Calentamiento FullBody"
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Tipo"
                name="preparation_type_id"
                rules={[
                  {
                    required: true,
                    message: 'El tipo es requerido',
                  },
                ]}
              >
                <Select
                  placeholder="Selecciona el tipo"
                  options={preparationTypes.map((pt) => ({
                    value: pt.id,
                    label: pt.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card style={{ borderRadius: 12 }}>
          <Title
            level={5}
            style={{
              marginTop: 0,
              marginBottom: 4,
            }}
          >
            Ejercicios
          </Title>

          <Text
            type="secondary"
            style={{
              display: 'block',
              marginBottom: 20,
            }}
          >
            Elige cualquier ejercicio del catálogo completo — no hay restricción por categoría.
          </Text>

          {/* Formulario de inclusión rápido */}
          <Row
            gutter={12}
            align="middle"
            style={{ marginBottom: 20 }}
          >
            <Col xs={24} sm={10}>
              <Select
                showSearch
                placeholder="Selecciona un ejercicio"
                value={newExercise.exerciseKey}
                onChange={(val) => {
                  const selectedExercise =
                    exercisesList[val];

                  setNewExercise({
                    ...newExercise,
                    exerciseKey: val,
                    exerciseId:
                      selectedExercise?.exercise_id,
                    equipmentId:
                      selectedExercise?.equipment_id ?? null,
                  });
                }}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={exercisesList.map((ex, index) => ({
                  value: index,
                  label: ex.displayLabel,
                }))}
                style={{ width: '100%' }}
                allowClear
              />
            </Col>

            <Col xs={12} sm={6}>
              <Select
                placeholder="Tipo de medición"
                value={newExercise.measurementTypeId}
                onChange={(val) =>
                  setNewExercise({
                    ...newExercise,
                    measurementTypeId: val,
                  })
                }
                options={measurementTypes.map((mt) => ({
                  value: mt.id,
                  label: mt.name,
                }))}
                style={{ width: '100%' }}
              />
            </Col>

            <Col xs={12} sm={5}>
              <InputNumber
                placeholder="Valor (ej. 10)"
                value={newExercise.value}
                onChange={(val) =>
                  setNewExercise({
                    ...newExercise,
                    value: val,
                  })
                }
                min={1}
                precision={0}
                step={1}
                style={{ width: '100%' }}
              />
            </Col>

            <Col xs={24} sm={3}>
              <Button
                type="default"
                icon={<PlusOutlined />}
                onClick={handleAddExercise}
                block
              >
                Agregar
              </Button>
            </Col>
          </Row>

          {/* Listado de ejercicios agregados */}
          {exercises.length === 0 ? (
            <Text type="danger">
              Debes agregar al menos un ejercicio a la mini-rutina.
            </Text>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {exercises.map((item, index) => (
                <Row
                  key={item.id || index}
                  gutter={12}
                  align="middle"
                >
                  <Col xs={24} sm={10}>
                    <Input
                      value={item.exercise_title}
                      disabled
                      style={{
                        color: 'rgba(0, 0, 0, 0.88)',
                      }}
                    />
                  </Col>

                  <Col xs={12} sm={6}>
                    <Select
                      value={item.measurement_type_id}
                      onChange={(val) =>
                        handleUpdateExerciseMeasurement(
                          index,
                          val
                        )
                      }
                      options={measurementTypes.map((mt) => ({
                        value: mt.id,
                        label: mt.name,
                      }))}
                      style={{ width: '100%' }}
                    />
                  </Col>

                  <Col xs={12} sm={5}>
                    <InputNumber
                      value={item.value}
                      onChange={(val) =>
                        handleUpdateExerciseValue(
                          index,
                          val
                        )
                      }
                      min={1}
                      precision={0}
                      step={1}
                      style={{ width: '100%' }}
                    />
                  </Col>

                  <Col xs={24} sm={3}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        handleRemoveExercise(index)
                      }
                    />
                  </Col>
                </Row>
              ))}
            </div>
          )}
        </Card>
      </Form>
    </div>
  );
};

export default PreparationUpsertPage;